// Activity (app flow §9). Rows in plain sentences, grouped by day; opening the page marks everything
// read. What was new when the page opened carries the rubric's mark at its edge (the same crimson
// stroke the navigation uses for the current place), not a dot per row.
import { createFileRoute, Link } from "@tanstack/react-router";
import { Avatar } from "../../components/Avatar";
import { Button, LinkButton } from "../../components/Button";
import { EmptyState } from "../../components/Empty";
import { Icon, type IconName } from "../../components/Icon";
import { ReactionGlyph } from "../../components/ReactionGlyph";
import { SectionHeader } from "../../components/SectionHeader";
import ui from "../../components/ui.module.css";
import { useFriendAction, useNotifications } from "../../data/queries";
import { REACTION_LABEL, type Notification } from "../../data/types";
import { dayHeading, longTime, shortTime } from "../../lib/time";
import { useTitle } from "../../lib/useTitle";
import { motionStyles } from "../../motion";
import s from "./notifications.module.css";

export const Route = createFileRoute("/_app/notifications")({ component: Activity });

/** A reader's words in quotes, unless they already open with a quote mark of their own. */
function Excerpt({ text }: { text: string }) {
  const own = /^["“‘'«„]/.test(text.trimStart());
  return <q className={own ? `${s.excerpt} ${s.ownQuotes}` : s.excerpt}>{text}</q>;
}

function sentence(n: Notification) {
  const others = n.others > 0 ? ` and ${n.others} ${n.others === 1 ? "other" : "others"}` : "";
  switch (n.kind) {
    case "friend_request":
      return <> asked to be friends</>;
    case "friend_accepted":
      return <> accepted your friend request</>;
    case "reaction":
      return (
        <>
          {others} responded to your post{n.reaction && <> with <strong>{REACTION_LABEL[n.reaction]}</strong></>}
        </>
      );
    case "plan_invite":
      return <> asked you to read <strong>{n.detail ?? n.excerpt ?? "a plan"}</strong> together</>;
    case "comment":
      return n.excerpt ? <> commented: <Excerpt text={n.excerpt} /></> : <> commented on your post</>;
    case "mention":
      return n.excerpt ? <> mentioned you: <Excerpt text={n.excerpt} /></> : <> mentioned you</>;
    case "report_outcome":
      return <>: {n.detail ?? "We looked at something you reported."}</>;
  }
}

/** The small mark beside the actor's face: what kind of news this is. A response shows its own glyph
 *  (the latest given, when several are grouped); one without a named response shows the Amen seal. */
const BADGE: Partial<Record<Notification["kind"], IconName>> = {
  comment: "comment",
  mention: "comment",
  friend_request: "user-plus",
  friend_accepted: "people.fill",
  plan_invite: "bible",
  report_outcome: "shield",
};

function Face({ n }: { n: Notification }) {
  const badge = BADGE[n.kind];
  return (
    <span className={s.face}>
      <Avatar user={n.actor ?? { displayName: "Bible Standard", avatarUrl: null }} size={40} />
      {(n.kind === "reaction" || badge) && (
        <span className={s.badge} aria-hidden="true">
          {n.kind === "reaction" ? <ReactionGlyph reaction={n.reaction ?? "amen"} on size={13} /> : <Icon name={badge!} size={12} />}
        </span>
      )}
    </span>
  );
}

/** The local calendar day of a moment, "2026-10-14". */
function dayOf(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function dayName(day: string): string {
  const today = dayOf(new Date().toISOString());
  const yesterday = dayOf(new Date(Date.now() - 86_400_000).toISOString());
  return day === today ? "Today" : day === yesterday ? "Yesterday" : dayHeading(day);
}

function Row({ n, onAccept }: { n: Notification; onAccept: (id: string) => void }) {
  return (
    <li className={n.read ? s.row : `${s.row} ${s.unread}`}>
      <Face n={n} />
      <div className={s.what}>
        <p>
          {!n.read && <span className="visually-hidden">New: </span>}
          {n.actor ? (
            <Link to="/@{$handle}" params={{ handle: n.actor.handle }} className={s.name}>
              {n.actor.displayName}
            </Link>
          ) : (
            <span className={s.name}>Bible Standard</span>
          )}
          {sentence(n)}{" "}
          <time className={s.time} dateTime={n.createdAt} title={longTime(n.createdAt)}>
            {shortTime(n.createdAt)}
          </time>
        </p>
        {n.postId && (
          <Link to="/p/$postId" params={{ postId: n.postId }} className={s.open}>
            View post
          </Link>
        )}
      </div>
      {n.kind === "plan_invite" && (
        <LinkButton to="/plans" variant="secondary" size="sm">
          See
        </LinkButton>
      )}
      {n.kind === "friend_request" && n.actor && (
        <Button variant="primary" size="sm" onClick={() => onAccept(n.actor!.id)}>
          Accept
        </Button>
      )}
    </li>
  );
}

function Activity() {
  useTitle("Activity");
  const { data: items, isPending } = useNotifications();
  const act = useFriendAction();
  const days: { day: string; rows: Notification[] }[] = [];
  for (const n of items ?? []) {
    const day = dayOf(n.createdAt);
    const last = days.at(-1);
    if (last?.day === day) last.rows.push(n);
    else days.push({ day, rows: [n] });
  }

  return (
    <div className={ui.page}>
      <header className={ui.pageHead}>
        <h1 className={ui.title}>Activity</h1>
      </header>
      {isPending ? null : days.length > 0 ? (
        <div className={motionStyles.arrive}>
          {days.map((g) => (
            <section key={g.day} className={s.day} aria-labelledby={`day-${g.day}`}>
              <SectionHeader id={`day-${g.day}`} title={dayName(g.day)} className={s.dayHead} />
              <ul>
                {g.rows.map((n) => (
                  <Row key={n.id} n={n} onAccept={(userId) => act.mutate({ action: "accept", userId })} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : (
        <EmptyState icon="bell" title="Nothing new." body="When friends respond to you, ask to be friends or invite you to a plan, you’ll see it here." />
      )}
    </div>
  );
}
